import { getInitials } from '../../lib/initials';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getComments, deleteComment, toggleCommentLike, hasUserLikedComment, notifyAllAboutPost } from '../../services/comments';
import { useAuth } from '../../hooks/useAuth';
import { CommentComposer } from './CommentComposer';
import { useState, useEffect } from 'react';

interface CommentListProps {
  postId: string;
}

export function CommentList({ postId }: CommentListProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isAdmin = Boolean(user?.isAdmin);

  const notifyAllMutation = useMutation({
    mutationFn: () => notifyAllAboutPost(postId),
    onSuccess: (data) => {
      alert(`Notifikation sendt til ${data.notifiedCount} brugere.`);
    },
  });

  const { data: comments, isLoading } = useQuery({
    queryKey: ['comments', postId],
    queryFn: () => getComments(postId),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteComment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', postId] });
      queryClient.invalidateQueries({ queryKey: ['posts'] });
    },
  });

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'Lige nu';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} min`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} t`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)} d`;

    return date.toLocaleDateString('da-DK', {
      day: 'numeric',
      month: 'short',
    });
  };

  if (isLoading) {
    return (
      <div className="px-4 py-3">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="border-t border-gray-200">
      {/* Comment composer */}
      <div className="px-4 pt-2 pb-2">
        <CommentComposer postId={postId} />
        {isAdmin && (
          <div className="mt-1 flex justify-end">
            <button
              onClick={() => notifyAllMutation.mutate()}
              disabled={notifyAllMutation.isPending}
              className="text-xs font-medium text-primary-600 hover:text-primary-800 disabled:opacity-50 transition-colors"
              title="Send notifikation om dette opslag til alle brugere"
            >
              {notifyAllMutation.isPending ? 'Sender...' : '🔔 Notificer alle'}
            </button>
          </div>
        )}
      </div>

      {/* Comments list */}
      {comments && comments.length > 0 && (
        <div className="px-4 pb-2 space-y-1">
          {comments.map((comment) => (
            <div key={comment.id} className="flex gap-2 items-start">
              {/* User avatar */}
              {comment.user?.profileImageUrl ? (
                <img
                  src={comment.user.profileImageUrl}
                  alt={comment.user?.name ?? 'Slettet bruger'}
                  className="w-7 h-7 rounded-full object-cover flex-shrink-0 mt-0.5"
                />
              ) : (
                <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-primary-600 font-semibold text-xs">
                    {getInitials(comment.user?.name ?? 'S')}
                  </span>
                </div>
              )}

              {/* Comment content */}
              <div className="flex-1 min-w-0">
                <div className="bg-gray-100 rounded-xl px-3 py-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-semibold text-xs text-gray-900 leading-snug">
                      {comment.user?.name ?? 'Slettet bruger'}
                    </p>
                    {user?.id === comment.userId && comment.userId != null && (
                      <button
                        onClick={() => deleteMutation.mutate(comment.id)}
                        disabled={deleteMutation.isPending}
                        className="text-xs text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50 flex-shrink-0"
                      >
                        Slet
                      </button>
                    )}
                  </div>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap break-words leading-snug">
                    {comment.content}
                  </p>
                </div>
                <div className="px-2 mt-0.5 flex items-center gap-3">
                  <span className="text-xs text-gray-400">
                    {formatTimeAgo(comment.createdAt)}
                  </span>
                  {user && <CommentLikeButton comment={comment} />}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CommentLikeButton({ comment }: { comment: any }) {
  const queryClient = useQueryClient();
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(comment._count?.likes || 0);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    hasUserLikedComment(comment.id).then(setLiked).catch(() => setLiked(false));
  }, [comment.id]);

  const likeMutation = useMutation({
    mutationFn: () => toggleCommentLike(comment.id),
    onMutate: async () => {
      // Optimistic update
      const newLiked = !liked;
      setLiked(newLiked);
      setLikeCount((prev: number) => (newLiked ? prev + 1 : prev - 1));
    },
    onSuccess: (data) => {
      setLiked(data.liked);
      queryClient.invalidateQueries({ queryKey: ['comments', comment.postId] });
    },
    onError: () => {
      // Revert on error
      setLiked(!liked);
      setLikeCount((prev: number) => (liked ? prev + 1 : prev - 1));
    },
  });

  const likedBy: Array<{ id: string; name: string }> = (comment.likes ?? []).map((l: any) => l.user);

  return (
    <div className="relative inline-flex">
      <button
        onClick={() => likeMutation.mutate()}
        disabled={likeMutation.isPending}
        onMouseEnter={() => likeCount > 0 && setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className={`text-xs font-medium transition-colors flex items-center space-x-1 ${
          liked ? 'text-red-600' : 'text-gray-500 hover:text-red-600'
        }`}
      >
        <span>{liked ? '❤️' : '🤍'}</span>
        {likeCount > 0 && <span>{likeCount}</span>}
      </button>
      {showTooltip && likedBy.length > 0 && (
        <div className="absolute bottom-full left-0 mb-1 z-50 bg-gray-800 text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap shadow-lg pointer-events-none max-w-xs">
          {likedBy.map((u) => u.name).join(', ')}
          <div className="absolute top-full left-4 border-4 border-transparent border-t-gray-800" />
        </div>
      )}
    </div>
  );
}
